import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import {
  LogIn,
  UserPlus,
  Eye,
  EyeOff,
  Copy,
  Check,
  ShieldCheck,
  KeyRound,
  Users as UsersIcon,
  History as HistoryIcon,
  Code2,
  Building2,
  Loader2,
  CheckCircle2,
  ScanLine,
  KeyRound as KeyRoundIcon,
} from "lucide-react";
import { APP_DISPLAY_NAME, APP_SHORT_ORG } from "@/branding";
import { DatalyLogo } from "@/components/DatalyLogo";
import { cn } from "@/lib/cn";
import {
  friendlyAuthError,
  listOidcProviders,
  login,
  mfaChallengeVerify,
  mfaEnrollConfirm,
  mfaEnrollInit,
  mfaRecoveryUse,
  oidcLoginUrl,
  register,
  setToken,
  type MfaEnrollInit,
  type OidcProvider,
  type RegisterResult,
} from "@/lib/api";

type Mode = "signin" | "signup";

/** Multi-step MFA state surfaced after a successful login/register that didn't issue a session. */
type MfaStep =
  | { kind: "challenge"; challengeToken: string; userEmail: string; useRecovery: boolean }
  | {
      kind: "enroll";
      enrollmentToken: string;
      userEmail: string;
      artifacts: MfaEnrollInit | null;
      recoveryCodes: string[] | null;
    };

const ORG_ROLE_LABEL: Record<RegisterResult["role"], string> = {
  dataly_admin: "Dataly admin",
  client_admin: "Client admin",
  client_staff: "Client staff",
  public_user: "Public",
};

type CreatedAccount = {
  email: string;
  password: string;
  organization: RegisterResult["organization"];
  role: RegisterResult["role"];
};

const FEATURES: { icon: typeof KeyRound; title: string; desc: string }[] = [
  { icon: KeyRound, title: "Vaults & secrets", desc: "Group encrypted values per environment" },
  { icon: ShieldCheck, title: "Role-based access", desc: "Admin / staff with per-vault ACL" },
  { icon: Code2, title: "API keys", desc: "Bearer tokens for CI / automation" },
  { icon: HistoryIcon, title: "Audit trail", desc: "Every action logged for compliance" },
  { icon: UsersIcon, title: "Team workspaces", desc: "Organizations with named members" },
  { icon: Building2, title: "Self-hosted", desc: "Your data, your tenant, your stack" },
];

export function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("signin");

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState<CreatedAccount | null>(null);
  const [mfaStep, setMfaStep] = useState<MfaStep | null>(null);
  const [oidcProviders, setOidcProviders] = useState<OidcProvider[]>([]);

  useEffect(() => {
    setShowPassword(false);
    setMessage(null);
  }, [mode]);

  useEffect(() => {
    let alive = true;
    listOidcProviders()
      .then((list) => {
        if (alive) setOidcProviders(list);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  // OIDC redirect lands the SPA back here with `?token=` (set by the API
  // after a successful provider sign-in). Promote it to localStorage and
  // route into the dashboard.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get("token");
    if (!tok) return;
    setToken(tok);
    params.delete("token");
    const newSearch = params.toString();
    const url = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
    window.history.replaceState({}, "", url);
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const submitDisabled = useMemo(() => {
    if (loading) return true;
    if (!email || password.length < 1) return true;
    if (mode === "signup") {
      if (password.length < 10) return true;
      if (!fullName.trim() || !companyName.trim()) return true;
    }
    return false;
  }, [loading, email, password, mode, fullName, companyName]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setLoading(true);
    try {
      if (mode === "signup") {
        const result = await register({
          email: email.trim(),
          password,
          fullName: fullName.trim(),
          phone: phone.trim(),
          companyName: companyName.trim(),
        });
        if (result.mfa_enroll_required && result.enrollment_token) {
          setMfaStep({
            kind: "enroll",
            enrollmentToken: result.enrollment_token,
            userEmail: result.user.email,
            artifacts: null,
            recoveryCodes: null,
          });
          // Defer the credentials modal until enrollment finishes.
          setCreated({
            email: result.user.email,
            password,
            organization: result.organization,
            role: result.role,
          });
        } else {
          setCreated({
            email: result.user.email,
            password,
            organization: result.organization,
            role: result.role,
          });
        }
      } else {
        const result = await login(email.trim(), password);
        if (result.kind === "signed_in") {
          navigate("/app", { replace: true });
          return;
        }
        if (result.kind === "mfa_required") {
          setMfaStep({
            kind: "challenge",
            challengeToken: result.challenge_token,
            userEmail: result.user.email,
            useRecovery: false,
          });
        } else if (result.kind === "mfa_enroll_required") {
          setMfaStep({
            kind: "enroll",
            enrollmentToken: result.enrollment_token,
            userEmail: result.user.email,
            artifacts: null,
            recoveryCodes: null,
          });
        }
      }
    } catch (err: unknown) {
      setMessage(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  const isSignup = mode === "signup";

  return (
    <>
      <div className="min-h-dvh bg-[var(--color-bg)] px-4 py-8 sm:py-10">
        {/* Header */}
        <header className="mx-auto max-w-2xl pb-7 text-center">
          <div className="mx-auto mb-4 flex justify-center">
            <DatalyLogo size="lg" className="rounded-xl" />
          </div>
          <h1 className="text-[1.6rem] font-semibold tracking-tight text-[var(--color-text)] sm:text-[1.8rem]">
            {APP_DISPLAY_NAME}
          </h1>
          <p className="mt-1.5 text-sm text-[var(--color-text-muted)]">
            {APP_SHORT_ORG} — encrypted vaults, role-based access, full audit trail.
          </p>
        </header>

        <div className="mx-auto flex max-w-[1100px] flex-wrap justify-center gap-6">
          {/* ───── Auth panel ───── */}
          <div
            className={cn(
              "flex-1 rounded-2xl bg-[var(--color-surface)] p-7 sm:p-8",
              mfaStep
                ? "min-w-[340px] max-w-[440px]"
                : isSignup
                  ? "min-w-[340px] max-w-[480px]"
                  : "min-w-[320px] max-w-[400px]",
            )}
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
          >
            {mfaStep ? (
              mfaStep.kind === "challenge" ? (
                <MfaChallengePanel
                  step={mfaStep}
                  setStep={setMfaStep}
                  onSuccess={() => navigate("/app", { replace: true })}
                />
              ) : (
                <MfaEnrollPanel
                  step={mfaStep}
                  setStep={setMfaStep}
                  onSuccess={() => {
                    if (created) {
                      // Credentials modal still pending — let it close + navigate.
                      setMfaStep(null);
                    } else {
                      navigate("/app", { replace: true });
                    }
                  }}
                />
              )
            ) : (
            <Tabs.Root value={mode} onValueChange={(v) => setMode(v as Mode)}>
              <Tabs.List className="mb-5 flex gap-1 rounded-[10px] bg-[var(--color-surface-3)] p-1">
                {([
                  { v: "signin", label: "Sign In" },
                  { v: "signup", label: "Create Account" },
                ] as const).map((t) => (
                  <Tabs.Trigger
                    key={t.v}
                    value={t.v}
                    className={cn(
                      "flex-1 rounded-lg py-2 text-sm font-medium transition-colors",
                      "data-[state=active]:bg-[var(--color-surface)] data-[state=active]:text-[var(--color-text)] data-[state=active]:shadow-sm",
                      "data-[state=inactive]:bg-transparent data-[state=inactive]:text-[var(--color-text-muted)] data-[state=inactive]:hover:text-[var(--color-text)]",
                    )}
                  >
                    {t.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>

              <Tabs.Content value="signin">
                <form onSubmit={submit} className="flex flex-col gap-3">
                  <Field label="Email">
                    <input
                      className="field-input"
                      type="text"
                      inputMode="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="Password">
                    <PasswordInput
                      value={password}
                      onChange={setPassword}
                      autoComplete="current-password"
                      show={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                    />
                  </Field>
                  {message && <ErrorBanner>{message}</ErrorBanner>}
                  <button
                    type="submit"
                    disabled={submitDisabled}
                    className="btn btn-primary mt-1 py-2.5 text-[0.9rem]"
                  >
                    {loading ? <Loader2 className="h-4 w-4 spin" /> : <LogIn className="h-4 w-4" />}
                    Sign In
                  </button>
                  {oidcProviders.length > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2 py-1">
                        <span className="h-px flex-1 bg-[var(--color-border)]" />
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-text-subtle)]">
                          or sign in with
                        </span>
                        <span className="h-px flex-1 bg-[var(--color-border)]" />
                      </div>
                      <div className="flex flex-col gap-2">
                        {oidcProviders.map((p) => (
                          <a
                            key={p.id}
                            href={oidcLoginUrl(p.id, window.location.origin + "/")}
                            className="btn btn-secondary justify-center py-2 text-sm"
                          >
                            <ScanLine className="h-4 w-4" />
                            Continue with {p.label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </form>
              </Tabs.Content>

              <Tabs.Content value="signup">
                <form onSubmit={submit} className="flex flex-col gap-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Full name">
                      <input
                        className="field-input"
                        type="text"
                        autoComplete="name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        minLength={2}
                      />
                    </Field>
                    <Field label="Company name">
                      <input
                        className="field-input"
                        type="text"
                        autoComplete="organization"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        required
                        minLength={2}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="Phone" hint="Optional">
                      <input
                        className="field-input"
                        type="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        placeholder="+91 90000 00000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        className="field-input"
                        type="text"
                        inputMode="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Field>
                  </div>
                  <Field label="Password" hint="At least 10 characters">
                    <PasswordInput
                      value={password}
                      onChange={setPassword}
                      autoComplete="new-password"
                      show={showPassword}
                      onToggle={() => setShowPassword((v) => !v)}
                    />
                  </Field>
                  {message && <ErrorBanner>{message}</ErrorBanner>}
                  <button
                    type="submit"
                    disabled={submitDisabled}
                    className="btn btn-primary mt-1 py-2.5 text-[0.9rem]"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 spin" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                    Create Account
                  </button>
                </form>
              </Tabs.Content>
            </Tabs.Root>
            )}

            {!mfaStep && (
              <p className="mt-5 text-center text-[11px] tracking-wide text-[var(--color-text-subtle)]">
                Encrypted at rest · audit-logged · vaults never leave your tenant
              </p>
            )}
          </div>

          {/* ───── Features panel ───── */}
          <div
            className="hidden flex-1 rounded-2xl bg-[var(--color-surface)] p-7 sm:p-8 md:block max-w-[640px] min-w-[340px]"
            style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
          >
            <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--color-text)]">
              <ShieldCheck className="h-4 w-4 text-[var(--color-accent)]" />
              What you get
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {FEATURES.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="rounded-xl bg-[var(--color-bg)] p-3.5"
                >
                  <Icon className="mb-2 h-5 w-5 text-[var(--color-accent)]" />
                  <p className="text-[0.85rem] font-semibold text-[var(--color-text)]">
                    {title}
                  </p>
                  <p className="mt-0.5 text-[0.78rem] text-[var(--color-text-muted)]">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl bg-[var(--color-bg)] p-3.5">
              <ShieldCheck className="h-4 w-4 text-[var(--color-accent)]" />
              <span className="text-xs font-medium text-[var(--color-text)]">
                Self-hosted on your stack
              </span>
              <span className="text-xs text-[var(--color-text-muted)]">
                Postgres-backed, JWT auth, scoped API keys.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* When signup forces MFA enrollment, postpone the credentials modal until enrollment completes. */}
      {created && !mfaStep && (
        <CreatedAccountModal
          account={created}
          onContinue={() => {
            setCreated(null);
            navigate("/app", { replace: true });
          }}
        />
      )}
    </>
  );
}

/* ─────────────── helper components ─────────────── */

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between text-[0.78rem] font-medium text-[var(--color-text)]">
        <span>{label}</span>
        {hint && (
          <span className="text-[11px] font-normal text-[var(--color-text-subtle)]">
            {hint}
          </span>
        )}
      </span>
      {children}
    </label>
  );
}

function PasswordInput({
  value,
  onChange,
  autoComplete,
  show,
  onToggle,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: "new-password" | "current-password";
  show: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="relative">
      <input
        className="field-input pr-11"
        type={show ? "text" : "password"}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        minLength={6}
        spellCheck={false}
      />
      <button
        type="button"
        className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
        onClick={onToggle}
        aria-pressed={show}
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="status"
      className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-[0.8rem] text-[var(--color-danger)]"
    >
      {children}
    </p>
  );
}

/* ─────────────── post-signup credentials modal ─────────────── */

/* ─────────────── MFA: signed-in challenge & first-time enrollment ─────────────── */

function MfaChallengePanel({
  step,
  setStep,
  onSuccess,
}: {
  step: Extract<MfaStep, { kind: "challenge" }>;
  setStep: (s: MfaStep | null) => void;
  onSuccess: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (step.useRecovery) {
        await mfaRecoveryUse(step.challengeToken, code.trim());
      } else {
        await mfaChallengeVerify(step.challengeToken, code.trim());
      }
      onSuccess();
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)]">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-lg font-semibold tracking-tight text-[var(--color-text)]">
          Two-factor authentication
        </h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Signed in as {step.userEmail}. Enter the 6-digit code from your authenticator app.
        </p>
      </div>
      <label className="block">
        <span className="mb-1 text-[0.78rem] font-medium text-[var(--color-text)]">
          {step.useRecovery ? "Recovery code" : "6-digit code"}
        </span>
        <input
          className="field-input text-center font-mono text-base tracking-[0.4em]"
          inputMode={step.useRecovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={step.useRecovery ? "abc12-de345" : "123456"}
        />
      </label>
      {err && (
        <p className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-[0.8rem] text-[var(--color-danger)]">
          {err}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || code.trim().length < 6}
        className="btn btn-primary mt-1 py-2.5 text-[0.9rem]"
      >
        {busy ? <Loader2 className="h-4 w-4 spin" /> : <ShieldCheck className="h-4 w-4" />}
        Verify
      </button>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          className="text-[var(--color-accent)] hover:underline"
          onClick={() =>
            setStep({ ...step, useRecovery: !step.useRecovery })
          }
        >
          {step.useRecovery ? "Use authenticator code" : "Use a recovery code"}
        </button>
        <button
          type="button"
          className="text-[var(--color-text-muted)] hover:underline"
          onClick={() => setStep(null)}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function MfaEnrollPanel({
  step,
  setStep,
  onSuccess,
}: {
  step: Extract<MfaStep, { kind: "enroll" }>;
  setStep: (s: MfaStep | null) => void;
  onSuccess: () => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (step.artifacts || step.recoveryCodes) return;
    let cancelled = false;
    (async () => {
      try {
        const a = await mfaEnrollInit(step.enrollmentToken);
        if (!cancelled) {
          setStep({ ...step, artifacts: a });
        }
      } catch (e: unknown) {
        if (!cancelled) setErr(friendlyAuthError(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, setStep]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const r = await mfaEnrollConfirm(step.enrollmentToken, code.trim());
      setStep({ ...step, recoveryCodes: r.recovery_codes });
    } catch (e: unknown) {
      setErr(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  if (step.recoveryCodes) {
    return (
      <RecoveryCodesPanel
        codes={step.recoveryCodes}
        userEmail={step.userEmail}
        onContinue={onSuccess}
      />
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)]">
          <ScanLine className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-lg font-semibold tracking-tight text-[var(--color-text)]">
          Set up two-factor authentication
        </h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Required for {step.userEmail}. Scan with Google Authenticator, 1Password, Authy, or similar.
        </p>
      </div>
      {step.artifacts ? (
        <>
          <div className="flex items-center justify-center rounded-xl bg-[var(--color-surface-2)] p-4">
            <img
              src={step.artifacts.qr_data_url}
              alt="MFA QR code"
              className="h-44 w-44"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="rounded-lg bg-[var(--color-surface-2)] px-3 py-2 text-xs">
            <button
              type="button"
              onClick={() => setShowSecret((v) => !v)}
              className="text-[var(--color-text-muted)] hover:underline"
            >
              {showSecret ? "Hide manual setup key" : "Can't scan? Show manual setup key"}
            </button>
            {showSecret && (
              <p className="mt-1 break-all font-mono text-[11px] text-[var(--color-text)]">
                {step.artifacts.secret}
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center py-6 text-[var(--color-text-muted)]">
          <Loader2 className="h-5 w-5 spin" />
        </div>
      )}
      <label className="block">
        <span className="mb-1 text-[0.78rem] font-medium text-[var(--color-text)]">
          Enter the 6-digit code from your app
        </span>
        <input
          className="field-input text-center font-mono text-base tracking-[0.4em]"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
        />
      </label>
      {err && (
        <p className="rounded-lg border border-[rgba(255,59,48,0.25)] bg-[var(--color-danger-bg)] px-3 py-2 text-[0.8rem] text-[var(--color-danger)]">
          {err}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || code.trim().length < 6 || !step.artifacts}
        className="btn btn-primary mt-1 py-2.5 text-[0.9rem]"
      >
        {busy ? <Loader2 className="h-4 w-4 spin" /> : <KeyRoundIcon className="h-4 w-4" />}
        Confirm & enable
      </button>
      <button
        type="button"
        className="text-center text-xs text-[var(--color-text-muted)] hover:underline"
        onClick={() => setStep(null)}
      >
        Cancel and sign out
      </button>
    </form>
  );
}

function RecoveryCodesPanel({
  codes,
  userEmail,
  onContinue,
}: {
  codes: string[];
  userEmail: string;
  onContinue: () => void;
}) {
  const [copied, setCopied] = useState(false);
  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked */
    }
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[var(--color-success-bg)] text-[var(--color-success)]">
          <CheckCircle2 className="h-5 w-5" />
        </div>
        <h2 className="mt-3 text-lg font-semibold tracking-tight text-[var(--color-text)]">
          MFA enabled
        </h2>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          Save these recovery codes — we will not show them again. Each code works once for {userEmail}.
        </p>
      </div>
      <div className="rounded-xl bg-[var(--color-surface-2)] p-3">
        <ul className="grid grid-cols-2 gap-1 font-mono text-[12px] text-[var(--color-text)]">
          {codes.map((c) => (
            <li key={c} className="rounded bg-[var(--color-surface)] px-2 py-1.5 text-center">
              {c}
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={copyAll}
          className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-surface-3)]"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy all"}
        </button>
      </div>
      <button type="button" className="btn btn-primary py-2.5" onClick={onContinue}>
        Continue
      </button>
    </div>
  );
}

function CredentialField({
  label,
  value,
  reveal = false,
}: {
  label: string;
  value: string;
  reveal?: boolean;
}) {
  const [shown, setShown] = useState(!reveal);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked */
    }
  }

  return (
    <div>
      <label className="block text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-[var(--color-text-subtle)]">
        {label}
      </label>
      <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2">
        <code className="flex-1 truncate font-mono text-sm text-[var(--color-text)]">
          {shown ? value : "••••••••••••"}
        </code>
        {reveal && (
          <button
            type="button"
            onClick={() => setShown((v) => !v)}
            className="rounded-md p-1.5 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
            aria-label={shown ? "Hide" : "Show"}
          >
            {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        <button
          type="button"
          onClick={copy}
          className="rounded-md p-1.5 text-[var(--color-text-muted)] transition hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
          aria-label="Copy"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>
      <p
        className={cn(
          "mt-1 flex h-3 items-center gap-1 text-xs text-[var(--color-success)] transition-opacity",
          copied ? "opacity-100" : "opacity-0",
        )}
        aria-live="polite"
      >
        <Check className="h-3 w-3" /> Copied
      </p>
    </div>
  );
}

function CreatedAccountModal({
  account,
  onContinue,
}: {
  account: CreatedAccount;
  onContinue: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="fade-in w-full max-w-md rounded-2xl bg-[var(--color-surface)] p-6"
        style={{ boxShadow: "var(--shadow-modal)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-full"
            style={{
              background: "var(--color-success-bg)",
              color: "#1f8b3a",
              border: "1px solid rgba(52,199,89,0.25)",
            }}
          >
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-[var(--color-text)]">
              Account created
            </h2>
            <p className="text-sm text-[var(--color-text-muted)]">
              Save these credentials — we don't email them.
            </p>
          </div>
        </div>

        <div className="my-5 divider" />

        <div className="space-y-3">
          <CredentialField label="Username (email)" value={account.email} />
          <CredentialField label="Password" value={account.password} reveal />
          <CredentialField label="Organization" value={account.organization.name} />
          <div>
            <label className="block text-[0.7rem] font-semibold uppercase tracking-[0.1em] text-[var(--color-text-subtle)]">
              Role
            </label>
            <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2">
              <span className="chip chip-blue">{ORG_ROLE_LABEL[account.role] ?? account.role}</span>
              <span className="text-xs text-[var(--color-text-muted)]">
                Default for new sign-ups.
              </span>
            </div>
          </div>
        </div>

        <p
          className="mt-4 rounded-lg px-3 py-2 text-xs"
          style={{
            background: "var(--color-accent-soft)",
            color: "var(--color-accent-hover)",
          }}
        >
          {account.role === "public_user" ? (
            <>
              You're signed in as <strong>Public</strong>. Your organization's{" "}
              <strong>Client admin</strong> can add you as <strong>Client staff</strong> by
              email so you get vault access. Platform <strong>Dataly admins</strong> can change
              roles from the Organization tab if needed.
            </>
          ) : (
            <>
              You're signed in as <strong>{ORG_ROLE_LABEL[account.role] ?? account.role}</strong>.
              Ask your <strong>Client admin</strong> or a <strong>Dataly admin</strong> if you need
              a different role for this workspace.
            </>
          )}
        </p>

        <button type="button" onClick={onContinue} className="btn btn-primary mt-5 w-full py-2.5">
          Continue to dashboard
        </button>
      </div>
    </div>
  );
}
